import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../utils';
import { partnerApiPath } from '../../apiPath';
import { partnerHttpClient } from '../../httpClient';

const PostPartnerBrandProductsOptionsRequestParamsSchema = z
  .object({
    brandId: z.string(),
  })
  .describe('PostPartnerBrandProductsOptionsRequestParamsSchema');

const PostPartnerBrandProductsOptionsRequestQuerySchema = z
  .object({
    platform: z.string(),
  })
  .describe('PostPartnerBrandProductsOptionsRequestQuerySchema');

const PostPartnerBrandProductsOptionsRequestBodySchema = z
  .object({
    goodsNos: z.array(z.number()).min(1).max(50),
  })
  .describe('PostPartnerBrandProductsOptionsRequestBodySchema');

const PostPartnerBrandProductsOptionsRequestSchema = z.object({
  params: PostPartnerBrandProductsOptionsRequestParamsSchema,
  query: PostPartnerBrandProductsOptionsRequestQuerySchema,
  body: PostPartnerBrandProductsOptionsRequestBodySchema,
});

export type PostPartnerBrandProductsOptionsRequest = z.infer<
  typeof PostPartnerBrandProductsOptionsRequestSchema
>;

const GoodsOptionValueSchema = z
  .object({
    valueId: z.number(),
    name: z.string(),
    sequence: z.number(),
  })
  .describe('GoodsOptionValueSchema');

const GoodsOptionSchema = z
  .object({
    optionIndex: z.number(),
    optionName: z.string(),
    values: z.array(GoodsOptionValueSchema),
  })
  .describe('GoodsOptionSchema');

const ExtraOptionValueSchema = z
  .object({
    valueId: z.number(),
    name: z.string(),
    sequence: z.number(),
  })
  .describe('ExtraOptionValueSchema');

const ExtraOptionSchema = z
  .object({
    extraOptionId: z.number(),
    name: z.string(),
    required: z.boolean(),
    sequence: z.number(),
    values: z.array(ExtraOptionValueSchema),
  })
  .describe('ExtraOptionSchema');

export type ExtraOptionValue = z.infer<typeof ExtraOptionValueSchema>;
export type ExtraOption = z.infer<typeof ExtraOptionSchema>;

const ProductOptionsSchema = z
  .object({
    goodsNo: z.number(),
    options: z.array(GoodsOptionSchema),
    extraOptions: z.array(ExtraOptionSchema),
  })
  .describe('ProductOptionsSchema');

export type ProductOptions = z.infer<typeof ProductOptionsSchema>;

const PostPartnerBrandProductsOptionsResponseDataSchema = z
  .object({
    goodsOptions: z.array(ProductOptionsSchema),
  })
  .describe('PostPartnerBrandProductsOptionsResponseDataSchema');

const PostPartnerBrandProductsOptionsResponseSchema = createApiResponseSchema(
  PostPartnerBrandProductsOptionsResponseDataSchema,
);

export type PostPartnerBrandProductsOptionsResponse = z.infer<
  typeof PostPartnerBrandProductsOptionsResponseSchema
>;

/** POST `/v1/partner/brands/{brandId}/products/options` — 상품 옵션 일괄 조회 */
export const postPartnerBrandProductsOptions = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(PostPartnerBrandProductsOptionsRequestSchema),
    async (request: PostPartnerBrandProductsOptionsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.post<PostPartnerBrandProductsOptionsResponse>(
          urlJoin(partnerApiPath.postBrandProductsOptions(request.params.brandId)),
          request.body,
          { params: request.query },
        ),
      ),
    validateApiResponse(PostPartnerBrandProductsOptionsResponseSchema),
  ),
);
